import moment from 'moment';

import {
  SellerBankAccountForm,
  SellerForm,
} from 'features/onlinePayment/state/config/types';
import {
  ClinicPhoneNumberRawData,
  ClinicPhoneNumberResponse,
  ClinicRawData,
  PhysicianRawData,
  SellerBankAccountRawData,
  SellerRawData,
} from 'features/onlinePayment/services/config/types';
import { cepFormatter } from 'shared/utils/formatters';
import { SellerType } from '../constants';

type ProfileFormInitialDataParams = {
  seller: SellerRawData | null | undefined;
  sellerForm: SellerForm | undefined;
  physician: PhysicianRawData | undefined;
  physicianId: number | undefined;
  sellerType?: SellerType;
  clinic: ClinicRawData | null | undefined;
  clinicId: number | string | null;
  clinicPhoneNumber: ClinicPhoneNumberRawData | null | undefined;
};

function resolveBirthdate(seller?: SellerRawData | null): string | Date | null {
  return seller?.birthdate ? moment(seller.birthdate).toDate() : null;
}

function resolveBusinessOpeningDate(
  seller?: SellerRawData | null,
): string | Date | null {
  return seller?.business_opening_date
    ? moment(seller.business_opening_date).toDate()
    : null;
}

function resolveAddress(
  seller?: SellerRawData | null,
  clinic?: ClinicRawData | null,
) {
  const {
    zip_code = clinic?.zip_code ?? '',
    street = clinic?.address ?? '',
    number = clinic?.number ?? '',
    neighborhood = clinic?.neighborhood ?? '',
    city = clinic?.city ?? '',
    state = clinic?.state ?? '',
    complement = clinic?.complement ?? '',
  } = seller?.address ?? {};

  return {
    zip_code,
    street,
    number: number?.toString() ?? '',
    neighborhood,
    city,
    state,
    complement,
  };
}

function resolveSellerType(
  seller: SellerRawData | null | undefined,
  sellerType?: SellerType,
) {
  return seller?.type ?? sellerType ?? SellerType.PF;
}

function resolvePersonalInfo(
  seller?: SellerRawData | null,
  profile?: PhysicianRawData['profile'] | null,
  clinicPhoneNumber?: ClinicPhoneNumberRawData | null,
) {
  return {
    name: seller?.name ?? profile?.name ?? '',
    email: seller?.email ?? profile?.email ?? '',
    cpf: seller?.cpf ?? profile?.cpf ?? '',
    phone:
      clinicPhoneNumber?.phone ?? seller?.phone ?? profile?.office_phone ?? '',
    initialPicture: profile?.thumb ?? '',
  };
}

function resolveCouncilInfo(
  seller?: SellerRawData | null,
  physician?: PhysicianRawData | null,
) {
  return {
    councilName: seller?.council_name ?? physician?.council_name ?? '',
    councilNumber: seller?.council_number ?? physician?.council_number ?? '',
    councilRegion: seller?.council_region ?? physician?.council_region ?? '',
    cbo: physician?.cbo,
  };
}

export function getLastClinicPhoneNumber(
  data: ClinicPhoneNumberResponse,
  clinicId: number | null,
): string | null {
  if (data.objects.length === 0) {
    return null;
  }

  const filteredObjects = data.objects.filter(
    (item) => item.clinic === clinicId,
  );

  if (filteredObjects.length === 0) {
    return null;
  }

  const lastItem = filteredObjects.reduce(
    (prev, curr) => ((curr.id ?? 0) > (prev.id ?? 0) ? curr : prev),
    { id: 0, phone: null },
  );

  return lastItem.phone;
}

function resolveBankInfo(sellerBankAccount?: SellerBankAccountRawData) {
  return {
    bank: sellerBankAccount?.bank_code ?? '',
    agency: sellerBankAccount?.routing_number ?? '',
    account: sellerBankAccount?.account_number ?? '',
    bankAccountId: sellerBankAccount?.id,
  };
}

function resolveInstallmentMinValue(minValue?: string) {
  return minValue ? parseFloat(minValue) : 0;
}

export function getProfileFormInitialData({
  seller,
  sellerForm,
  physician,
  physicianId,
  sellerType,
  clinic,
  clinicPhoneNumber,
}: ProfileFormInitialDataParams): SellerForm {
  if (sellerForm) return sellerForm;

  const profile = physician?.profile;
  const birthdate = resolveBirthdate(seller);
  const businessOpeningDate = resolveBusinessOpeningDate(seller);
  const { zip_code, street, number, neighborhood, city, state, complement } =
    resolveAddress(seller, clinic);

  const type = resolveSellerType(seller, sellerType);
  const { name, email, cpf, phone, initialPicture } = resolvePersonalInfo(
    seller,
    profile,
    clinicPhoneNumber,
  );
  const { councilName, councilNumber, councilRegion, cbo } = resolveCouncilInfo(
    seller,
    physician,
  );

  return {
    cbo,
    physicianId: physicianId ?? 0,
    type,
    initialPicture,
    picture: '',
    name,
    councilName,
    councilNumber,
    councilRegion,
    email,
    cpf,
    birthdate,
    phone,
    zipCode: cepFormatter(zip_code),
    street,
    number,
    neighborhood,
    city,
    state,
    complement,
    businessName: seller?.business_name ?? clinic?.name ?? '',
    businessDescription: seller?.business_description ?? '',
    businessOpeningDate,
    businessCnpj: seller?.business_cnpj ?? clinic?.clinic_cnpj ?? '',
  };
}

export function getBankAccountInitialData(
  data: SellerBankAccountForm | undefined,
  seller: SellerRawData | null | undefined,
  sellerBankAccount?: SellerBankAccountRawData,
): SellerBankAccountForm {
  if (data) return data;

  const {
    id: sellerId,
    name,
    cpf,
    type,
    business_cnpj,
    business_name,
    min_installment_value,
    max_installments,
  } = seller ?? {};

  const { bank, agency, account, bankAccountId } =
    resolveBankInfo(sellerBankAccount);
  const minValue = resolveInstallmentMinValue(min_installment_value);

  return {
    type: type ?? SellerType.PF,
    name: name ?? '',
    cpf: cpf ?? '',
    businessName: business_name ?? '',
    businessCnpj: business_cnpj ?? '',
    sellerId: sellerId ?? '',
    bank,
    agency,
    account,
    bankAccountId,
    installments: max_installments ?? '1',
    minValue,
    terms: false,
    ...(bankAccountId && { bankAccountId }),
  } as SellerBankAccountForm;
}
